import { FC, ReactNode, useCallback, useEffect, useState } from 'react';

import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Button, ButtonProps } from '@shared/components/button-new';
import { Overlay, OverlayBody, OverlayFooter, OverlayHeader } from '@shared/components/overlay';
import { ContainerWidths } from '@shared/constants/common';
import { ButtonSize } from '@shared/types/common/button';

import styles from './Confirmation.styles';

export type ConfirmationButtonsProps = {
  confirm?: Partial<ButtonProps>;
  cancel?: Partial<ButtonProps>;
};

export interface ConfirmationProps extends WithStyles<typeof styles> {
  buttonProps?: ConfirmationButtonsProps;
}

let showConfirmationFn: any;

export function showConfirmation(
  {
    withCloseButton,
    title,
    question,
    buttonProps,
  }: {
    withCloseButton?: boolean;
    title: ReactNode;
    question: ReactNode;
    buttonProps?: ConfirmationButtonsProps;
  },
  callback: (isConfirmed: boolean) => void
): void {
  showConfirmationFn({ title, question, buttonProps, withCloseButton }, callback);
}

interface ConfirmationState {
  open: boolean;
  withCloseButton?: boolean;
  title: React.ReactNode;
  question: React.ReactNode;
  buttonProps: ConfirmationButtonsProps;
  callback?: (isConfirmed?: boolean) => void;
}

const DEFAULT_STATE: ConfirmationState = {
  open: false,
  withCloseButton: true,
  title: '',
  question: '',
  buttonProps: {
    confirm: {
      text: t('continue'),
    },
    cancel: {
      text: t('cancel'),
    },
  },
  callback: undefined,
};

const ConfirmationComponent: FC<ConfirmationProps> = ({ classes }) => {
  const [state, setState] = useState(DEFAULT_STATE);

  useEffect(() => {
    showConfirmationFn = show;
  }, []);

  const show = useCallback(({ title, question, buttonProps, withCloseButton }, callback) => {
    setState((prevState) => ({
      ...prevState,
      title,
      question,
      callback,
      withCloseButton,
      open: true,
      buttonProps: { ...prevState.buttonProps, ...buttonProps },
    }));
  }, []);

  const terminate = useCallback(() => {
    if (state.callback) {
      state.callback(false);
    }
    hide();
  }, [state]);

  const hide = useCallback(() => {
    setState((prevState) => ({ ...prevState, open: false }));
    setTimeout(() => setState(DEFAULT_STATE), 500);
  }, []);

  const handleYes = useCallback(() => {
    if (state.callback) {
      state.callback(true);
    }
    hide();
  }, [state]);

  const { open, title, question, buttonProps } = state;

  return (
    <Overlay
      classes={{ paper: classes.root }}
      open={open}
      withTransition
      onClose={terminate}
      width={ContainerWidths.xs}
    >
      <OverlayHeader heading={title} />
      <OverlayBody classes={{ root: classes.content }}>{question}</OverlayBody>
      <OverlayFooter
        contentRight={
          <>
            <Button size={ButtonSize.mediumLegacy} {...buttonProps.cancel} onClick={terminate} />
            <Button size={ButtonSize.mediumLegacy} {...buttonProps.confirm} onClick={handleYes} />
          </>
        }
      />
    </Overlay>
  );
};

export const Confirmation = withStyles(styles)(ConfirmationComponent);
